import React from 'react';
import { Helmet } from 'react-helmet';

// Organization structured data
const OrganizationSchema = () => {
  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Optifyne',
    url: 'https://optifyne.com',
    logo: 'https://optifyne.com/images/optifyne_horizontal.png',
    sameAs: [
      // Add your social profiles here when available
       'https://www.linkedin.com/company/optifyne/?utm_source=linkedin&utm_medium=referral&utm_campaign=website',
       'https://twitter.com/optifyneltd',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '',
      contactType: 'customer service',
      email: 'enquiries@optifyne.com',
      availableLanguage: 'English'
    },
    description: 'Optifyne delivers secure process automation for government and enterprise. UK-based security-cleared developers with 15+ years of experience.'
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(orgSchema)}
      </script>
    </Helmet>
  );
};

// Service structured data
const ServiceSchema = ({ name, description, url }) => {
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: name,
    provider: {
      '@type': 'Organization',
      name: 'Optifyne'
    },
    description: description,
    url: url
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(serviceSchema)}
      </script>
    </Helmet>
  );
};

// BreadcrumbList structured data
const BreadcrumbSchema = ({ items }) => {
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
    </Helmet>
  );
};

// WebPage structured data
const WebPageSchema = ({ title, description, url }) => {
  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    description: description,
    url: url
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(webPageSchema)}
      </script>
    </Helmet>
  );
};

// LocalBusiness structured data
const LocalBusinessSchema = () => {
  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: 'Optifyne',
    image: 'https://optifyne.com/images/optifyne_horizontal.png',
    '@id': 'https://optifyne.com',
    url: 'https://optifyne.com',
    telephone: '+44 (0) 330 043 7550',
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'UK',
      addressLocality: 'United Kingdom'
    },
    geo: {
      '@type': 'GeoCoordinates'
      // Add latitude and longitude when available
    },
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday'
      ],
      opens: '09:00',
      closes: '17:00'
    },
    sameAs: [
      // Add social profiles when available
    ],
    priceRange: '££',
    servesCuisine: 'Process Automation Solutions'
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(localBusinessSchema)}
      </script>
    </Helmet>
  );
};

// FAQ structured data
const FAQSchema = ({ questions }) => {
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: questions.map(q => ({
      '@type': 'Question',
      name: q.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: q.answer
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>
    </Helmet>
  );
};

export { OrganizationSchema, ServiceSchema, BreadcrumbSchema, WebPageSchema, LocalBusinessSchema, FAQSchema };
